import { Badge } from "@/components/ui/Badge";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Play, Target, Trash2 } from "lucide-react";
import type { CoachingClip, CoachItemReach } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { VideoClipThumbnail } from "@/features/coach-review/components/VideoClipThumbnail";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import {
  useCoachClipMutations,
  useCoachClipsList,
} from "./hooks/useCoachClipsList";
import { PageHeader } from "./components/PageHeader";
import { BulkSelectionBar, SelectToggle } from "./components/BulkSelectionBar";
import type { GoalComposerSource } from "./components/GoalComposerModal";
import { ReachChip } from "./components/ReachChip";
import { useItemReach } from "./hooks/useItemReach";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import { playerAudienceLabel, visibilityLabel } from "./feedbackLibraryLabels";
import { useRoster } from "./hooks/useRoster";
import {
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABELS,
} from "@/features/coach-review/components/Composer";
import { useMatches } from "@/features/matches/useMatches";

/**
 * Coach > Clips list page (M6a).
 *
 * Ports the legacy `renderCoachClips` (`js/coaching/clips.js`):
 * the team's coaching clips, newest-edited first, each with a
 * thumbnail tile, a `Clip · {category}` meta line, and the
 * per-row action set.
 *
 * Per-row actions:
 *   - **Preview** → opens the M5 `<FeedbackPlayerModal>` in clip
 *     mode (the window-bounded player). The clip carries
 *     `match_id` + `team_id`, so the modal's `match` prop is
 *     built straight from the clip — no extra fetch.
 *   - **Edit** → `/coach/review?clip_id=…` — Coach Review's M5
 *     clip-edit mode (the clip composer lives there).
 *   - **Delete** — confirm + `deleteCoachingClip`.
 *
 * "+ New clip" → `/coach/review?source=video` — Coach Review's
 * video surface, where the coach flips to clip mode and saves a
 * window (the M4→M6 creation-routing carry-in; same `?source=`
 * plumbing the Notes page uses).
 */

export type ClipsPageProps = {
  /** M7.5 D2: opens the goal composer with the clip as source.
   * Undefined ⇒ the row's "+ New goal" button is hidden. */
  onCreatePlayerGoal?: (playerId: string, source: GoalComposerSource) => void;
};

export function ClipsPage({ onCreatePlayerGoal }: ClipsPageProps = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const clipsQuery = useCoachClipsList();
  const players = useRoster().data?.players ?? [];
  const matches = useMatches().data ?? [];
  const itemReach = useItemReach();
  const mutations = useCoachClipMutations();

  // Newest-edited first — legacy sort.
  const clips = useMemo(() => {
    const list = clipsQuery.data?.clips ?? [];
    return [...list].sort((a, b) =>
      String(b.updated_at).localeCompare(String(a.updated_at)),
    );
  }, [clipsQuery.data]);

  const [categoryFilter, setCategoryFilter] = useState("");

  // Do not retain a category that has become invisible because the authorized
  // collection dropped to the five-item controls threshold.
  useEffect(() => {
    if (clips.length <= 5) setCategoryFilter("");
  }, [clips.length]);
  const collection = useCollection(clips, {
    scope: "ClipsPage.tsx",
    text: (item) => {
      const playerNames = players
        .filter((player) => item.player_ids?.includes(player.id))
        .map((player) => player.display_name)
        .join(" ");
      return `${item.title} ${item.description} ${item.category} ${item.visibility} ${item.match_id} ${playerNames}`;
    },
    matches: (item, visibility) => item.visibility === visibility,
    predicate: (item) => !categoryFilter || item.category === categoryFilter,
  });
  const [deleting, setDeleting] = useState<number | null>(null);
  // The clip currently open in the focused-player preview modal.
  const [previewClip, setPreviewClip] = useState<CoachingClip | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CoachingClip | null>(null);

  // A6: bulk selection — same shape as the Notes page.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  const selectedIds = useMemo(
    () => clips.filter((c) => selected.has(c.id)).map((c) => c.id),
    [clips, selected],
  );
  const selectedUntaggedCount = useMemo(
    () =>
      clips.filter(
        (c) => selected.has(c.id) && (c.player_ids?.length ?? 0) === 0,
      ).length,
    [clips, selected],
  );

  const handleDelete = (clip: CoachingClip) => {
    setConfirmDelete(clip);
  };

  const confirmDeleteClip = async () => {
    if (!confirmDelete) return;
    const clip = confirmDelete;
    setConfirmDelete(null);
    setDeleting(clip.id);
    try {
      await mutations.deleteClip(clip.id);
      toast.success("Clip deleted");
    } catch (err) {
      toast.error("Couldn't delete clip", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkVisibility = async (visibility: CoachingVisibility) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { ok, failed } = await mutations.bulkUpdateVisibility(
        selectedIds,
        visibility,
      );
      if (failed === 0) {
        toast.success(`Updated visibility on ${ok} clip${ok === 1 ? "" : "s"}`);
      } else {
        toast.error(
          `Updated ${ok}, failed ${failed} — check the rows that didn't change.`,
        );
      }
      clearSelection();
    } catch (err) {
      toast.error("Couldn't update visibility", {
        description: errorMessage(err),
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkDeleteClips = async () => {
    setConfirmBulkDelete(false);
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { ok, failed } = await mutations.bulkDelete(selectedIds);
      if (failed === 0) {
        toast.success(`Deleted ${ok} clip${ok === 1 ? "" : "s"}`);
      } else {
        toast.error(`Deleted ${ok}, failed ${failed}.`);
      }
      clearSelection();
    } catch (err) {
      toast.error("Couldn't delete clips", { description: errorMessage(err) });
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="clips-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete clip?"
          message={`Delete "${confirmDelete.title?.trim() || "this clip"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          initialFocus="close"
          onConfirm={confirmDeleteClip}
          onClose={() => setConfirmDelete(null)}
          data-testid="clips-delete-confirm"
        />
      ) : null}
      {confirmBulkDelete ? (
        <ConfirmDialog
          title="Delete selected clips?"
          message={`Delete ${selectedIds.length} clip${selectedIds.length === 1 ? "" : "s"}? This can't be undone.`}
          confirmLabel="Delete"
          danger
          initialFocus="close"
          onConfirm={confirmBulkDeleteClips}
          onClose={() => setConfirmBulkDelete(false)}
          data-testid="clips-bulk-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Feedback Clips"
        description={
          <>
            Saved video evidence for player or team feedback. Each row shows
            audience, visibility, source window, and the next coach action.
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() =>
              navigate({ to: "/coach/review", search: { source: "video" } })
            }
            data-testid="clips-new-clip"
            aria-label="New clip"
            title="New clip"
            size="sm"
            className="library-create-action"
          >
            New clip
          </Button>
        }
      />

      <CollectionToolbar
        collection={collection}
        label="clips"
        searchPlaceholder="Title, player, match or category"
        extraFiltersAfter
        extraFilters={[
          {
            label: "Category",
            ariaLabel: "Filter clips by category",
            value: categoryFilter,
            clearValue: "",
            options: [
              { value: "", label: "All categories" },
              ...NOTE_CATEGORIES.map((value) => ({
                value,
                label: NOTE_CATEGORY_LABELS[value],
              })),
            ],
            onChange: setCategoryFilter,
          },
        ]}
        filterLabel="Visibility"
        filters={[
          { value: "", label: "All audiences" },
          { value: "team", label: "Team" },
          { value: "player", label: "Player + family" },
          { value: "coach", label: "Coaches" },
        ]}
      />
      {collection.controls && collection.filteredTotal === 0 ? (
        <p className="text-sm text-ink-muted">
          No results. Clear filters to see all items.
        </p>
      ) : null}
      {clipsQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="clips-loading"
        >
          Loading clips…
        </p>
      ) : clips.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="clips-empty"
        >
          No coaching clips yet. Use <strong>+ New clip</strong> in Coach Review
          to save a video window.
        </p>
      ) : (
        <>
          <BulkSelectionBar
            count={selectedIds.length}
            busy={bulkBusy}
            untaggedWarning={
              selectedUntaggedCount > 0
                ? `${selectedUntaggedCount} selected ${selectedUntaggedCount === 1 ? "clip has" : "clips have"} no tagged players — "Player + family" won't reach a player for ${selectedUntaggedCount === 1 ? "it" : "them"}.`
                : null
            }
            onSetVisibility={handleBulkVisibility}
            onDelete={() => setConfirmBulkDelete(true)}
            onClear={clearSelection}
            testIdPrefix="clips"
          />
          <ul className="flex flex-col gap-2" data-testid="clips-list">
            {collection.items.map((clip) => (
              <ClipRow
                key={clip.id}
                clip={clip}
                reach={itemReach.get("clip", clip.id)}
                deleting={deleting === clip.id}
                selected={selected.has(clip.id)}
                onToggleSelected={() => toggleSelected(clip.id)}
                onPreview={() => setPreviewClip(clip)}
                onEdit={() =>
                  navigate({
                    to: "/coach/review",
                    search: {
                      match_id: clip.match_id,
                      clip_id: clip.id,
                      source: "video",
                    },
                  })
                }
                onDelete={() => handleDelete(clip)}
                // M7.5 D2: per-row "+ New goal" — gated on the clip
                // having at least one tagged player (legacy parity).
                onCreateGoal={
                  onCreatePlayerGoal && clip.player_ids?.length
                    ? () =>
                        onCreatePlayerGoal(clip.player_ids![0], {
                          source_clip_id: clip.id,
                          seedTitle: clip.title?.trim() || undefined,
                          seedDescription:
                            clip.description?.trim() || undefined,
                        })
                    : undefined
                }
              />
            ))}
          </ul>
        </>
      )}

      <CollectionPagination collection={collection} />

      {/* Focused feedback player — clip mode (M5 modal). The clip
       * carries `match_id` + `team_id`, so the `match` prop is
       * built straight from it. */}
      {previewClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: previewClip }}
          match={{
            id: previewClip.match_id,
            team_id: previewClip.team_id,
          }}
          onClose={() => setPreviewClip(null)}
          presentation="coach"
          players={players}
          matchLabel={(() => {
            const match = matches.find((item) => item.id === previewClip.match_id);
            return match
              ? `${match.home_team} vs ${match.away_team}${match.date ? ` · ${match.date}` : ""}`
              : previewClip.match_id;
          })()}
          onEdit={() => {
            const clip = previewClip;
            setPreviewClip(null);
            navigate({
              to: "/coach/review",
              search: {
                match_id: clip.match_id,
                clip_id: clip.id,
                source: "video",
              },
            });
          }}
          onCreateGoal={
            onCreatePlayerGoal && previewClip.player_ids?.length
              ? () => {
                  const clip = previewClip;
                  setPreviewClip(null);
                  onCreatePlayerGoal(clip.player_ids![0], {
                    source_clip_id: clip.id,
                    seedTitle: clip.title?.trim() || undefined,
                    seedDescription: clip.description?.trim() || undefined,
                  });
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}

type ClipRowProps = {
  /** Phase 2.3: per-item delivery. Undefined until the engagement
   * query lands, or for an unassigned (team-wide) clip. */
  reach?: CoachItemReach;
  clip: CoachingClip;
  deleting: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** M7.5 D2: undefined ⇒ no goal button. */
  onCreateGoal?: () => void;
};

function ClipRow({
  clip,
  reach,
  deleting,
  selected,
  onToggleSelected,
  onPreview,
  onEdit,
  onDelete,
  onCreateGoal,
}: ClipRowProps) {
  const metaParts = [
    playerAudienceLabel(clip.player_ids),
    `Player sees: ${visibilityLabel(clip.visibility)}`,
    `Source: ${formatPlayhead(clip.start_seconds)}-${formatPlayhead(clip.end_seconds)}`,
    `Category: ${clip.category}`,
    "Next: Preview or goal",
  ];
  return (
    <li
      className="grid gap-2 rounded border border-rule bg-surface p-2 shadow-sm sm:grid-cols-[auto_4rem_minmax(0,1fr)_auto] sm:items-center"
      data-testid="clip-row"
      data-clip-id={clip.id}
      data-selected={selected ? "true" : "false"}
    >
      <SelectToggle
        selected={selected}
        onToggle={onToggleSelected}
        label={selected ? "Deselect clip" : "Select clip"}
        testid="clip-select-toggle"
      />
      <ClipThumb clip={clip} />
      <div className="min-w-0 flex-1">
        <p className="eyebrow library-audience">
          {playerAudienceLabel(clip.player_ids)}
        </p>
        <h3 className="library-item-title">
          {clip.title?.trim() || "(untitled clip)"}
        </h3>
        {clip.description ? (
          <p className="library-item-description">{clip.description}</p>
        ) : null}
        <div className="library-item-meta">
          <Badge variant="muted">{visibilityLabel(clip.visibility)}</Badge>
          <Badge variant="accent">{clip.category}</Badge>
          <span>
            {formatPlayhead(clip.start_seconds)}–
            {formatPlayhead(clip.end_seconds)}
          </span>
          <ReachChip reach={reach} />
        </div>
        <span className="sr-only">{metaParts.join(" · ")}</span>
      </div>
      <div className="library-row-actions">
        <RowButton
          label="Open"
          testid="clip-action-preview"
          onClick={onPreview}
          icon={<Play size={16} strokeWidth={2} aria-hidden="true" />}
        />
        <RowButton
          label="Edit"
          testid="clip-action-edit"
          onClick={onEdit}
          icon={<Pencil size={16} strokeWidth={2} aria-hidden="true" />}
        />
        {/* M7.5 D2: per-row "+ New goal", gated on the clip
         * having a tagged player. */}
        {onCreateGoal ? (
          <RowButton
            label="+ New goal"
            testid="clip-action-goal"
            onClick={onCreateGoal}
            icon={<Target size={16} strokeWidth={2} aria-hidden="true" />}
          />
        ) : null}
        <RowButton
          label="Delete"
          testid="clip-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
          icon={<Trash2 size={16} strokeWidth={2} aria-hidden="true" />}
        />
      </div>
    </li>
  );
}

/** Clip thumbnail tile — the access-controlled JPEG via the M5
 * `useThumbnail` primitive; film-strip placeholder until it
 * resolves / on failure. */
function ClipThumb({ clip }: { clip: CoachingClip }) {
  return (
    <VideoClipThumbnail
      clip={clip}
      testId="clip-thumb"
      placeholderTestId="clip-thumb-placeholder"
      overlayTestId="clip-thumb-overlay"
      className="relative block h-11 w-16 shrink-0 overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface"
    />
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          : "border-rule text-ink-muted hover:border-accent hover:bg-accent/10 hover:text-accent",
      ].join(" ")}
    >
      <span className="library-action-icon">{props.icon}</span>
      <span className="library-action-label">
        {props.label.replace(/^\+ /, "")}
      </span>
    </button>
  );
}
